/**
 * Events Store (Zustand)
 * 
 * Global events state management
 * - Events list with loading and error states
 * - CRUD operations (fetch, create, update, delete)
 * - Season filtering logic
 * - Selected event tracking
 * 
 * Requirements: 2.1, 2.2, 2.3, 2.6, 2.7, 2.8
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { restClient, Event, CreateEventDto, UpdateEventDto } from '../api/rest-client';
import { mergeEventUpdate } from '../utils/eventUpdate';

interface EventsState {
  // State
  events: Event[];
  selectedEvent: Event | null;
  selectedSeason: string | null;
  loading: boolean;
  error: string | null;

  // Computed
  filteredEvents: Event[];

  // Actions
  fetchEvents: (season?: string, userRiderIds?: string[]) => Promise<void>;
  selectEvent: (id: string) => Promise<void>;
  createEvent: (event: CreateEventDto) => Promise<Event>;
  /**
   * Update an event.
   *
   * Returns `void`, not `Event`: the backend returns only a status message, so a
   * `Promise<Event>` signature was never satisfiable -- it was a lie the compiler
   * was told to believe, and it is what allowed the response body to be written
   * into state as if it were an event. With no return value there is nothing left
   * to be wrong. (BR-33)
   */
  /**
   * `options.resetRsvps` clears every rider's RSVP for the event.
   *
   * It is an OPTION rather than a DTO field on purpose: this action merges the
   * DTO into cached events via `mergeEventUpdate`, so a flag placed in the DTO
   * would be spliced onto every cached `Event` as a phantom attribute. The flag
   * is forwarded to the API and excluded from the merge. (Property P-4.)
   */
  updateEvent: (
    id: string,
    event: UpdateEventDto,
    options?: { resetRsvps?: boolean }
  ) => Promise<void>;
  deleteEvent: (id: string) => Promise<void>;
  setSelectedSeason: (season: string | null) => void;
  clearError: () => void;
  clearSelectedEvent: () => void;
}

/**
 * Get current season (current year as string)
 */
function getCurrentSeason(): string {
  return new Date().getFullYear().toString();
}

/**
 * Events Store
 * 
 * Manages events state globally using Zustand
 */
export const useEventsStore = create<EventsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      events: [],
      selectedEvent: null,
      selectedSeason: getCurrentSeason(),
      loading: false,
      error: null,

      // Computed property - filtered events by season
      get filteredEvents() {
        const { events, selectedSeason } = get();
        
        if (!selectedSeason) {
          return events;
        }

        return events.filter(event => event.season === selectedSeason);
      },

      /**
       * Fetch events from API
       * Requirements: 2.1, 2.2, 2.3
       */
      fetchEvents: async (season?: string, userRiderIds?: string[]) => {
        set({ loading: true, error: null });

        try {
          // For Events page: pass null to fetch all events
          // For Calendar page: pass season string to filter by season
          // If nothing passed, use selectedSeason from store
          const seasonToFetch = season === null ? undefined : (season || get().selectedSeason);
          console.log('[EventsStore] fetchEvents called:', { 
            seasonParam: season, 
            selectedSeasonFromStore: get().selectedSeason,
            seasonToFetch,
            userRiderIds,
          });
          
          const events = await restClient.getEvents(seasonToFetch || undefined, userRiderIds);

          set({
            events,
            loading: false,
            error: null,
          });
        } catch (error) {
          console.error('Failed to fetch events:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch events',
            loading: false,
          });
        }
      },

      /**
       * Select and fetch a single event by ID
       * Requirements: 2.4
       * 
       * Note: Legacy API doesn't support GET /events/{id} endpoint (returns 403)
       * Instead, we fetch all events first if not loaded, then select from the list.
       * 
       * Always re-fetches from API to ensure complete event data is available.
       * The dashboard puts partial event objects (missing event_checkin, season, etc.)
       * into the store, so we can't rely on cached data for the detail view.
       */
      selectEvent: async (id: string) => {
        set({ loading: true, error: null });

        try {
          // Always fetch full event data from the API.
          // Dashboard stores partial events (missing event_checkin, season, etc.)
          // so we need the complete data for the detail view.
          const events = await restClient.getEvents(undefined);
          
          const event = events.find(e => e.event_id === id);
          
          if (event) {
            set({
              events,
              selectedEvent: event,
              loading: false,
              error: null,
            });
            return;
          }

          // Event not found — update events list anyway
          set({ events, error: null });
          throw new Error('Event not found');
        } catch (error) {
          console.error('Failed to fetch event:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch event',
            loading: false,
          });
        }
      },

      /**
       * Create a new event
       * Requirements: 2.6
       */
      createEvent: async (eventDto: CreateEventDto) => {
        set({ loading: true, error: null });

        try {
          const newEvent = await restClient.createEvent(eventDto);

          // Add to events list
          set(state => ({
            events: [...state.events, newEvent],
            loading: false,
            error: null,
          }));

          return newEvent;
        } catch (error) {
          console.error('Failed to create event:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to create event',
            loading: false,
          });
          throw error;
        }
      },

      /**
       * Update an existing event
       * Requirements: 2.7
       */
      updateEvent: async (
        id: string,
        eventDto: UpdateEventDto,
        options?: { resetRsvps?: boolean }
      ) => {
        set({ loading: true, error: null });

        try {
          // NOTE: `PUT /events` (events-update.py) returns only
          // `{ message: 'Event updated successfully' }` -- NOT the updated event,
          // despite restClient.updateEvent being typed `Promise<Event>`.
          //
          // This response body was previously written straight into `events[]`
          // and `selectedEvent`, which wiped `event_status` and made the Cancel
          // button reappear right after a successful cancellation. So we call it
          // for its side effect only and DISCARD the body. (BR-32)
          // Only pass the third argument when there is actually something to
          // pass. Calling with an explicit `undefined` would change the
          // observable call shape for every existing caller -- and an existing
          // test legitimately asserts that the DTO is forwarded unchanged as a
          // two-argument call. This keeps that contract exactly intact.
          if (options) {
            await restClient.updateEvent(id, eventDto, options);
          } else {
            await restClient.updateEvent(id, eventDto);
          }

          // Apply the fields we actually sent onto the event we already hold.
          // Fields absent from the DTO are preserved. (BR-31)
          //
          // A bulk RSVP reset needs an extra step: the server cleared
          // event_expectedAttendance, but `reset_rsvps` is deliberately NOT a
          // DTO field (see the interface docblock), and mergeEventUpdate only
          // applies keys that ARE present in the DTO. Without clearing it here
          // the UI would keep displaying the responses that were just deleted.
          const applyUpdate = (e: Event): Event => {
            const merged = mergeEventUpdate(e, eventDto);
            return options?.resetRsvps
              ? { ...merged, event_expectedAttendance: {} }
              : merged;
          };

          set(state => ({
            events: state.events.map(e => (e.event_id === id ? applyUpdate(e) : e)),
            selectedEvent: state.selectedEvent?.event_id === id
              ? applyUpdate(state.selectedEvent)
              : state.selectedEvent,
            loading: false,
            error: null,
          }));

          if (options?.resetRsvps) {
            // RSVP state is never served from the stable cache, but the
            // dashboard response cache is separate and would otherwise still
            // hold the old counts. (FR-3.6)
            restClient.clearDashboardCache();
          }
        } catch (error) {
          console.error('Failed to update event:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to update event',
            loading: false,
          });
          throw error;
        }
      },

      /**
       * Delete an event
       * Requirements: 2.8
       */
      deleteEvent: async (id: string) => {
        set({ loading: true, error: null });

        try {
          await restClient.deleteEvent(id);

          // Remove from events list
          set(state => ({
            events: state.events.filter(e => e.event_id !== id),
            selectedEvent: state.selectedEvent?.event_id === id 
              ? null 
              : state.selectedEvent,
            loading: false,
            error: null,
          }));
        } catch (error) {
          console.error('Failed to delete event:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to delete event',
            loading: false,
          });
          throw error;
        }
      },

      /**
       * Set selected season for filtering
       * Requirements: 2.2, 2.3
       */
      setSelectedSeason: (season: string | null) => {
        set({ selectedSeason: season });
      },

      /**
       * Clear error state
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Clear selected event
       */
      clearSelectedEvent: () => {
        set({ selectedEvent: null });
      },
    }),
    { name: 'EventsStore' }
  )
);

// Selectors for convenience
export const selectEvents = (state: EventsState) => state.events;
export const selectFilteredEvents = (state: EventsState) => state.filteredEvents;
export const selectSelectedEvent = (state: EventsState) => state.selectedEvent;
export const selectSelectedSeason = (state: EventsState) => state.selectedSeason;
export const selectEventsLoading = (state: EventsState) => state.loading;
export const selectEventsError = (state: EventsState) => state.error;

export default useEventsStore;
